import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { Layout, Menu } from "antd";
import { useTranslation } from "react-i18next";
import Logo from "../../shared/assets/logistics.png";
import usePermissions from "../../hooks/usePermissions";
import { fetchSystemConfig, readCachedSystemConfig } from "../../shared/lib/systemConfig";
import { resolveHomePath } from "../../shared/lib/homePath";
import {
  // AppstoreOutlined, // was old dashboard icon
  UserOutlined,
  GiftOutlined,
  ShopOutlined,
  // BarChartOutlined, // Financial Report (hidden)
  // LineChartOutlined, // Sales report (hidden)
  OrderedListOutlined,
  TeamOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  TranslationOutlined,
  ClearOutlined,
  ImportOutlined,
  BankOutlined,
  CalculatorOutlined,
  DollarOutlined,
  RollbackOutlined,
  StopOutlined,
  SafetyOutlined,
  PrinterOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import "../../shared/styles/Sidebar.css";

const { Sider } = Layout;

const Sidebar = ({ collapsed, onCollapse, cartCount }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { hasPermission, hasAny, permissions, isAdmin } = usePermissions();
  const homePath = resolveHomePath(permissions, isAdmin);
  const [systemConfig, setSystemConfig] = useState(() => readCachedSystemConfig());
  const brandName = systemConfig?.site_name || t("app.brand.defaultName", { defaultValue: "IQ Price" });

  useEffect(() => {
    fetchSystemConfig()
      .then(setSystemConfig)
      .catch(() => null);
  }, []);

  const getSelectedKeys = () => {
    const path = location.pathname;
    // /pos is handled outside this layout, but keep key for consistency if remounted
    if (path === "/") return ["pos"];
    if (path.startsWith("/customers") || path.startsWith("/customers")) return ["customers"];
    if (path.startsWith("/products") || path.startsWith("/products")) return ["products"];
    if (path.startsWith("/roles")) return ["roles"];
    if (path.startsWith("/users")) return ["users"];
    if (path.startsWith("/settings/system")) return ["system-settings"];
    if (path.startsWith("/settings/translations")) return ["translations"];
    if (path.startsWith("/settings/maintenance")) return ["maintenance"];
    if (path === "/orders" || path.startsWith("/orders/") || path === "/orders" || path.startsWith("/orders")) return ["orders"];
    if (path === "/unpaid-sales") return ["unpaid-sales"];
    if (path === "/cash-management") return ["cash-management"];
    if (path.startsWith("/returns")) return ["returns"];
    if (path.startsWith("/cancellations")) return ["cancellations"];
    if (path === "/shop" || path === "/HomeShop") return ["homeshop"];
    if (path === "/cart" || path === "/CartShop") return ["cartshop"];
    if (path === "/pos/register") return ["pos-register"];
    if (path === "/pos") return ["pos"];
    if (path === "/reports/financial") return ["reports-financial"];
    if (path === "/audit" || path.startsWith("/audit/")) return ["system-audit"];
    if (path === "/reports/accountant/audit" || path.startsWith("/reports/accountant/audit")) return ["system-audit"];
    if (path === "/reports/accountant" || path.startsWith("/reports/accountant/")) return ["reports-accountant"];
    if (path === "/reports/farmers-finance" || path.startsWith("/farmers-finance")) return ["farmer-settlements"];
    if (path === "/Reports" || path === "/reports/selling") return ["reports-selling"];
    if (path === "/currency-totals") return ["currency-totals"];
    if (path.startsWith("/farmer-expenses")) return ["farmer-settlements"];
    if (path.startsWith("/company-expenses")) return ["company-expenses"];
    if (path.startsWith("/farmer-settlements")) return ["farmer-settlements"];
    if (path.startsWith("/farmer-settlement-archives")) return ["farmer-settlement-archives"];
    if (path.startsWith("/farmer-payments")) return ["farmer-settlements"];
    if (path.startsWith("/vendors")) return ["vendors"];
    return [];
  };

  const menuItems = useMemo(() => {
    const pick = (item) => {
      if (item.requiredPermission && !hasPermission(item.requiredPermission)) {
        return null;
      }
      if (item.requiredAny?.length && !hasAny(item.requiredAny)) {
        return null;
      }
      const { requiredPermission, requiredAny, ...rest } = item;
      return rest;
    };

    const SectionLabel = ({ children }) => (
      <span className="sidebar-section-label">{children}</span>
    );

    const items = [];

    // Old bird's-eye dashboard — hidden; POS is the main "Dashboard".
    // const dash = pick({
    //   key: "dashboard",
    //   icon: <AppstoreOutlined />,
    //   label: <Link to="/">{t("dashboard")}</Link>,
    //   requiredPermission: "dashboard.view",
    // });
    // if (dash) items.push(dash);

    // POS first — labeled Dashboard in the menu.
    const posDash = pick({
      key: "pos",
      icon: <ShopOutlined />,
      label: <Link to="/pos">{t("dashboard")}</Link>,
      requiredPermission: "pos.sell",
    });
    if (posDash) items.push(posDash);

    const salesChildren = [
      pick({
        key: "orders",
        icon: <OrderedListOutlined />,
        label: <Link to="/orders">{t("orders")}</Link>,
        // Cashiers (POS) or desk users with sales-history only
        requiredAny: ["pos.sell", "orders.index"],
      }),
      pick({
        key: "returns",
        icon: <RollbackOutlined />,
        label: <Link to="/returns">{t("posReturnsTitle", "Returns")}</Link>,
        requiredAny: ["pos.returns_view", "pos.return"],
      }),
      pick({
        key: "cancellations",
        icon: <StopOutlined />,
        label: <Link to="/cancellations">{t("posCancellationsTitle", "Cancellations")}</Link>,
        requiredAny: ["pos.cancel", "pos.returns_view", "pos.return", "orders.index"],
      }),
      pick({
        key: "pos-register",
        icon: <OrderedListOutlined />,
        label: <Link to="/pos/register">{t("dailyRegisterTitle")}</Link>,
        requiredAny: ["pos.sell", "orders.index", "data.daily_register.all"],
      }),
      // DISABLED: pick({ key: "homeshop", icon: <ShopOutlined />, label: <Link to="/shop">{t("homeShop")}</Link>, requiredPermission: "orders.add" }),
      // DISABLED: pick({ key: "cartshop", icon: <ShoppingCartOutlined />, label: <Link to="/cart">{t("cartShop")}</Link>, requiredPermission: "orders.add" }),
    ].filter(Boolean);

    if (salesChildren.length) {
      items.push({
        type: "group",
        label: <SectionLabel>{t("sidebarSectionSales")}</SectionLabel>,
        children: salesChildren,
      });
    }

    const contactsChildren = [
      pick({
        key: "customers",
        icon: <UserOutlined />,
        label: <Link to="/customers">{t("customers")}</Link>,
        requiredPermission: "customers.index",
      }),
      pick({
        key: "vendors",
        icon: <ImportOutlined />,
        label: <Link to="/vendors">{t("vendorsMenu")}</Link>,
        requiredPermission: "vendors.index",
      }),
    ].filter(Boolean);

    if (contactsChildren.length) {
      items.push({
        type: "group",
        label: <SectionLabel>{t("sidebarSectionContacts")}</SectionLabel>,
        children: contactsChildren,
      });
    }

    const companyChildren = [
      pick({
        key: "company-expenses",
        icon: <BankOutlined />,
        label: <Link to="/company-expenses">{t("companyExpensesTitle", "Company Expenses")}</Link>,
        requiredAny: ["company_expenses.view", "company_expenses.add", "company_expenses.cancel", "company_expenses.manage"],
      }),
    ].filter(Boolean);

    if (companyChildren.length) {
      items.push({
        type: "group",
        label: <SectionLabel>{t("sidebarSectionCompany", "Company")}</SectionLabel>,
        children: companyChildren,
      });
    }

    const farmersFinanceChildren = [
      pick({
        key: "farmer-settlements",
        icon: <DollarOutlined />,
        label: <Link to="/farmer-settlements">{t("farmerSettlementWorkspace", "Farmer Finance")}</Link>,
        requiredAny: ["vendors.finance", "vendors.settle", "vendors.payment"],
      }),
      pick({
        key: "farmer-settlement-archives",
        icon: <PrinterOutlined />,
        label: <Link to="/farmer-settlement-archives">{t("farmerArchivesTitle", "Settlement Archives")}</Link>,
        requiredPermission: "vendors.settle",
      }),
    ].filter(Boolean);

    if (farmersFinanceChildren.length) {
      items.push({
        type: "group",
        label: <SectionLabel>{t("sidebarSectionFarmersFinance", "Farmers Finance")}</SectionLabel>,
        children: farmersFinanceChildren,
      });
    }

    const catalogChildren = [
      // Units / categories removed from app UI (routes redirect to products).
      pick({
        key: "products",
        icon: <GiftOutlined />,
        label: <Link to="/products">{t("products")}</Link>,
        requiredPermission: "products.index",
      }),
      // Stock page hidden from catalog nav
    ].filter(Boolean);

    if (catalogChildren.length) {
      items.push({
        type: "group",
        label: <SectionLabel>{t("sidebarSectionCatalog")}</SectionLabel>,
        children: catalogChildren,
      });
    }

    const reportsChildren = [
      pick({
        key: "reports-accountant",
        icon: <CalculatorOutlined />,
        label: <Link to="/reports/accountant">{t("accountantDashboardTitle", "Accountant Dashboard")}</Link>,
        requiredPermission: "reports.view",
      }),
      hasPermission("reports.unpaid_sales")
        ? {
          key: "unpaid-sales",
          icon: <DollarOutlined />,
          label: <Link to="/unpaid-sales">{t("unpaidSalesTitle", "Unpaid Sales")}</Link>,
        }
        : null,
      hasPermission("cash.view")
        ? {
          key: "cash-management",
          icon: <WalletOutlined />,
          label: <Link to="/cash-management">{t("cashManagementTitle", "Cash Management")}</Link>,
        }
        : null,
    ].filter(Boolean);

    if (reportsChildren.length) {
      items.push({
        type: "group",
        label: <SectionLabel>{t("sidebarSectionReports")}</SectionLabel>,
        children: reportsChildren,
      });
    }

    const adminChildren = [
      pick({
        key: "roles",
        icon: <SafetyCertificateOutlined />,
        label: <Link to="/roles">{t("roles")}</Link>,
        requiredPermission: "roles.index",
      }),
      pick({
        key: "users",
        icon: <TeamOutlined />,
        label: <Link to="/users">{t("users")}</Link>,
        requiredPermission: "users.index",
      }),
      hasPermission("reports.audit")
        ? {
          key: "system-audit",
          icon: <SafetyOutlined />,
          label: <Link to="/audit">{t("accReversalAudit", "System Audit")}</Link>,
        }
        : null,
    ].filter(Boolean);

    if (adminChildren.length) {
      items.push({
        type: "group",
        label: <SectionLabel>{t("sidebarAdministration")}</SectionLabel>,
        children: adminChildren,
      });
    }

    const settingsChildren = [
      pick({
        key: "system-settings",
        icon: <SettingOutlined />,
        label: <Link to="/settings/system">{t("systemSettings")}</Link>,
        requiredPermission: "users.index",
      }),
      pick({
        key: "translations",
        icon: <TranslationOutlined />,
        label: <Link to="/settings/translations">{t("settingsTranslationsTitle", "Languages")}</Link>,
        requiredPermission: "maintenance.reset",
      }),
      hasPermission("maintenance.reset") || hasPermission("users.index")
        ? {
          key: "maintenance",
          icon: <ClearOutlined />,
          label: <Link to="/settings/maintenance">{t("maintenanceMenu")}</Link>,
        }
        : null,
    ].filter(Boolean);

    if (settingsChildren.length) {
      items.push({
        type: "group",
        label: <SectionLabel>{t("sidebarSectionSettings")}</SectionLabel>,
        children: settingsChildren,
      });
    }

    return items;
  }, [t, hasPermission, hasAny]);

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={onCollapse}
      width={260}
      collapsedWidth={72}
      className="app-sidebar"
      theme="dark"
      breakpoint="lg"
    >
      <div className="sidebar-brand">
        <Link to={homePath} className="sidebar-brand-link">
          <img
            src={systemConfig?.sidebar_logo_url || Logo}
            alt={t("app.brand.logoAlt", {
              defaultValue: "{{brand}} logo",
              brand: brandName,
            })}
            className="sidebar-logo"
          />
          {!collapsed && (
            <span className="sidebar-brand-text" title={brandName}>
              {brandName}
            </span>
          )}
        </Link>
      </div>
      <Menu
        mode="inline"
        selectedKeys={getSelectedKeys()}
        items={menuItems}
        className="sidebar-menu"
      />
    </Sider>
  );
};

export default Sidebar;
